/**
 * `/app/me/families` — the households this person is in, and the way to start another
 * (issues #301 and #360).
 *
 * The `familyId` is in the route (ADR 0021 decision 2), so the panel needs somewhere to get
 * one; the GET is it. **The list is not what makes a family readable** — every screen's request
 * is verified again by `resolveAssertedFamily`, which re-reads the membership, the role and
 * whether the family still exists. A switcher entry is an offer, not an authorization.
 *
 * **The copy is the server's**, on both calls. `nextStep` is the same sentence `get_started`
 * and every family-tool refusal give a person with nowhere to go (#158), and every refusal the
 * create can produce arrives as a `message` this app renders rather than replaces — see
 * `packages/identity/src/memberships.ts` and `createFamily.ts`.
 */

import type { MePayloads } from "@pilotfish/api/src/app/contract.ts";
import { type AppApi, appFetch } from "@pilotfish/app-api";

/**
 * **The switcher's list, as the server answers it** — `MePayloads["GET /families"]`, derived
 * from `me.ts` rather than described here (ADR 0025 decisions 3 and 4).
 *
 * The two things worth knowing survive the swap because they are the server's own, not this
 * file's: `name` is `null` for a household whose record is already gone — never a placeholder
 * — and a `deleting` family stays in the list on purpose, because telling that member "you
 * have no family, create one" sends them into a create flow that refuses them, which is the
 * loop #228 broke.
 */
export type Memberships = MePayloads["GET /families"];

/**
 * One household the caller belongs to.
 *
 * `role` is the caller's own standing and nothing about anybody else's (#360, ADR 0023
 * decision 8) — which is what keeps the wireframes' *"with Priya and Dan"* out of it: that
 * would be a roster read per family on an endpoint answering about this person alone.
 */
export type Membership = Memberships["families"][number];

export function fetchMemberships(api: AppApi): Promise<Memberships> {
  return appFetch<Memberships>(api, "/app/me/families");
}

/**
 * **What a create answers with** — `MePayloads["POST /families"]`, derived.
 *
 * `alreadyCreated` is **not an error state**: a double-clicked button and a lost response are
 * the same request twice, and the server answers both with the household it made.
 * `accountEmail` is ADR 0024 decision 2's disclosure, returned rather than enforced — the
 * weakest of the three on this surface, since the panel already names the signed-in account on
 * its own chrome.
 */
export type StartedFamily = MePayloads["POST /families"];

export function startFamily(
  api: AppApi,
  input: { name: string; displayName?: string },
): Promise<StartedFamily> {
  const timezone = browserTimezone();
  return appFetch<StartedFamily>(api, "/app/me/families", {
    method: "POST",
    body: {
      name: input.name,
      ...(input.displayName === undefined ? {} : { displayName: input.displayName }),
      ...(timezone === undefined ? {} : { timezone }),
    },
  });
}

/**
 * The browser's IANA timezone, or `undefined` if it will not say.
 *
 * Wrapped because `resolvedOptions()` is the one call in this submit that can throw on an
 * exotic runtime, and a household that could not be started because its timezone could not be
 * read would be an absurd failure. The same guard the login app's create carries.
 */
function browserTimezone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === "string" && zone.length > 0 ? zone : undefined;
  } catch {
    return undefined;
  }
}

/** The families a member can actually open — what the switcher offers. */
export function openFamilies(memberships: Memberships): readonly Membership[] {
  return memberships.families.filter((family) => family.status === "active");
}
